import React, { useState, useCallback } from "react";
import useColorScheme from "../hooks/useColorScheme";
import ColorPickerScheme from "./PostColorScheme/ColorPickerScheme";
import chroma from "chroma-js";
import { darkOrLightColor } from "../helpers/darkOrLightColor";
import {
  shades_section,
  shades_title,
  shades_grid,
  shade_color,
  actions_bar,
  action_btn,
  toast,
} from "../styles/modules/color_picker_scheme.module.css";

// Textos multiidioma
const TEXTS = {
  es: {
    copyAll: "Copiar escala",
    copyCSS: "Copiar CSS",
    copied: "¡Copiado!",
  },
  en: {
    copyAll: "Copy scale",
    copyCSS: "Copy CSS",
    copied: "Copied!",
  },
  pt: {
    copyAll: "Copiar escala",
    copyCSS: "Copiar CSS",
    copied: "Copiado!",
  },
  fr: {
    copyAll: "Copier l'échelle",
    copyCSS: "Copier CSS",
    copied: "Copié!",
  },
  de: {
    copyAll: "Skala kopieren",
    copyCSS: "CSS kopieren",
    copied: "Kopiert!",
  },
};

function SchemesColorsPage({
  color = "#0080ff",
  schemeName = "complement",
  titleScheme = "Esquema complementario",
  tittleForShades,
  locale,
}) {
  const texts = TEXTS[locale] || TEXTS.es;
  const [mainColor, setMainColor, colorScheme] = useColorScheme(
    color,
    schemeName
  );
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  // Generar escala de colores
  const colorScale = colorScheme
    ? chroma.scale(colorScheme).mode("lch").colors(10)
    : [];

  // Mostrar toast
  const displayToast = useCallback(message => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  }, []);

  // Copiar color individual
  const copyColor = useCallback(
    colorHex => {
      navigator.clipboard.writeText(colorHex);
      displayToast(`${texts.copied} ${colorHex}`);
    },
    [displayToast, texts.copied]
  );

  // Copiar toda la escala
  const copyAllScale = useCallback(() => {
    const colorsText = colorScale.join(", ");
    navigator.clipboard.writeText(colorsText);
    displayToast(texts.copied);
  }, [colorScale, displayToast, texts.copied]);

  // Copiar como CSS
  const copyCSSVariables = useCallback(() => {
    const css = `:root {\n${colorScale
      .map((c, i) => `  --scale-color-${i + 1}: ${c};`)
      .join("\n")}\n}`;
    navigator.clipboard.writeText(css);
    displayToast(texts.copied);
  }, [colorScale, displayToast, texts.copied]);

  return (
    <>
      {/* Selector de color y esquema */}
      <ColorPickerScheme
        mainColor={mainColor}
        setMainColor={setMainColor}
        colorScheme={colorScheme}
        schemeName={titleScheme}
        locale={locale}
      />

      {/* Sección de escala de colores */}
      <section className={shades_section}>
        <h2 className={shades_title}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <line x1="3" y1="9" x2="21" y2="9" />
            <line x1="3" y1="15" x2="21" y2="15" />
          </svg>
          {tittleForShades}
        </h2>

        {/* Grid de colores de la escala */}
        <div className={shades_grid}>
          {colorScale.map((colorHex, i) => (
            <div
              key={i}
              className={shade_color}
              style={{
                backgroundColor: colorHex,
                color: darkOrLightColor(colorHex),
              }}
              onClick={() => copyColor(colorHex.toUpperCase())}
              title={colorHex.toUpperCase()}
            >
              <span>{colorHex.toUpperCase().slice(1)}</span>
            </div>
          ))}
        </div>

        {/* Acciones */}
        <div className={actions_bar}>
          <button className={action_btn} onClick={copyAllScale}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
            {texts.copyAll}
          </button>
          <button className={action_btn} onClick={copyCSSVariables}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="16 18 22 12 16 6" />
              <polyline points="8 6 2 12 8 18" />
            </svg>
            {texts.copyCSS}
          </button>
        </div>
      </section>

      {/* Toast */}
      {showToast && (
        <div className={toast}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          {toastMessage}
        </div>
      )}
    </>
  );
}

export default SchemesColorsPage;
